import React, { useState, useRef, useEffect } from 'react';
import { authFetch, isController } from '../../common/auth';
import { openContent } from '../FilterPickerLocal/FilterPickerLocal';
import StorageHelper from '../../Helpers/StorageHelper';
import { playablesOf } from '../../common/episodes';
import { titleImage } from '../../common/posters';
import { startRecording, voiceSupported, micErrorMessage } from '../../common/voiceInput';
import './AIChat.css';
import { TRANSLATE_EVENT } from '../AutoFilter/translateJobs';

const withToken = (url) => {
    const token = localStorage.getItem('rc_auth_token');
    return url && token ? `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}` : url;
};
const fileUrl = (domain, kind, folder, name) => withToken(`${domain}/${kind}/${folder}/${name}`);
const titleOf = (folder) => folder.split('/').pop();

function ResultCard({ domain, item, onPlay }) {
    const [open, setOpen] = useState(false);
    const imageUrl = titleImage(domain, item);
    const playables = playablesOf(domain, item);
    const isSeries = playables.length > 1;
    const play = (index) => onPlay({ playables, index, image: imageUrl });
    return (
        <div className={`ai-card${open ? ' ai-card--open' : ''}`}>
            <button className="ai-card-main" onClick={() => (isSeries ? setOpen(o => !o) : play(0))} title={isSeries ? 'Show episodes' : `Play ${titleOf(item.folder)}`}>
                <div className="ai-card-poster" style={imageUrl ? { backgroundImage: `url("${imageUrl}")` } : undefined}>
                    {!imageUrl && <span className="ai-card-poster-fallback">🎬</span>}
                    {isSeries
                        ? <span className="ai-card-badge">{playables.length} ep</span>
                        : <span className="ai-card-play">▶</span>}
                </div>
                <span className="ai-card-title">{titleOf(item.folder)}</span>
            </button>
            {open && (
                <div className="ai-card-episodes">
                    {playables.map((p, i) => (
                        <button key={p.video} className="ai-card-episode" onClick={() => play(i)} title={p.name}>▶ {p.name}</button>
                    ))}
                </div>
            )}
        </div>
    );
}

// Progress of a background job (a subtitle translation) started from the chat.
function JobStatus({ domain, job, onDone }) {
    const [state, setState] = useState({ status: 'running', progress: 0, label: job.label, message: '' });
    const onDoneRef = useRef(onDone);
    onDoneRef.current = onDone;
    useEffect(() => {
        let stopped = false;
        let timer;
        const poll = async () => {
            try {
                const res = await authFetch(`${domain}/api/v1/ai/jobs/${job.id}`);
                if (res.ok) {
                    const data = await res.json();
                    if (stopped) return;
                    setState(data);
                    if (data.status !== 'running') { if (data.status === 'done') onDoneRef.current?.(); return; }
                } else if (res.status === 404) {
                    if (!stopped) setState(s => ({ ...s, status: 'error', message: 'This job is no longer tracked (the server restarted?)' }));
                    return;
                }
            } catch {}
            if (!stopped) timer = setTimeout(poll, 2000);
        };
        poll();
        return () => { stopped = true; clearTimeout(timer); };
    }, [domain, job.id]);
    const pct = Math.round((state.progress || 0) * 100);
    return (
        <div className={`ai-job ai-job--${state.status}`}>
            <div className="ai-job-label">{state.label}</div>
            {state.status === 'running'
                ? <div className="ai-job-bar"><div className="ai-job-bar-fill" style={{ width: `${Math.max(pct, 3)}%` }} /></div>
                : <div className="ai-job-message">{state.status === 'done' ? `✓ ${state.message}` : `✕ ${state.message}`}</div>}
        </div>
    );
}

// What the assistant remembers about this account: preference notes (it adds them itself, or
// when asked) and recent requests. Both are sent to the AI with every question.
function MemoryPanel({ domain }) {
    const [memory, setMemory] = useState(null);
    const [error, setError] = useState('');
    const request = async (query, method = 'GET') => {
        try {
            const res = await authFetch(`${domain}/api/v1/ai/memory${query}`, { method });
            if (!res.ok) throw new Error(`Server error (${res.status})`);
            setMemory(await res.json());
            setError('');
        } catch (e) { setError(e.message); }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { request(''); }, [domain]);
    if (error) return <div className="ai-memory"><div className="ai-memory-empty">{error}</div></div>;
    if (!memory) return <div className="ai-memory"><div className="ai-memory-empty">Loading…</div></div>;
    return (
        <div className="ai-memory">
            <div className="ai-memory-title">What the AI remembers about you</div>
            {memory.notes.length === 0 && <div className="ai-memory-empty">Nothing yet — tell it what you like (e.g. "I love sci-fi, no horror").</div>}
            {memory.notes.map((n, i) => (
                <div key={`${i}-${n.text}`} className="ai-memory-note">
                    <span>{n.text}</span>
                    <button title="Forget this" onClick={() => request(`?note=${i}`, 'DELETE')}>✕</button>
                </div>
            ))}
            <div className="ai-memory-footer">
                <span>{memory.searches.length} recent request{memory.searches.length === 1 ? '' : 's'} remembered</span>
                {memory.searches.length > 0 && <button onClick={() => request('?searches=1', 'DELETE')}>Clear requests</button>}
                {(memory.notes.length > 0 || memory.searches.length > 0) && (
                    <button onClick={() => { if (window.confirm('Forget everything the AI remembers about you?')) request('', 'DELETE'); }}>Forget all</button>
                )}
            </div>
        </div>
    );
}

// item (optional): a library entry ({ folder, files }) the chat is about — opened from a Store
// card's AI button. onChanged: called after the AI added files to it (subtitles).
// remote (optional): the chat is Remote Control's and can operate the screen —
// { getScreen(): the screen's status, runActions(actions): carries out what the AI decided }.
export default function AIChat({ domain, onClose, item, onChanged, remote, initialMessages }) {
    // Same as the Store: a phone paired as a remote sends it to the screen; otherwise it plays here.
    const play = async ({ playables, index, image }) => {
        const p = playables[index];
        StorageHelper.saveToCurrentList({ videos: playables.map(x => x.video), srts: playables.map(x => x.srt), filters: playables.map(x => x.filter), index });
        if (!isController()) { openContent({ video: p.video, srt: p.srt, filter: p.filter, image }); return; }
        try {
            const res = await authFetch(`${domain}/api/v1/remote/play`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ videoPath: p.video, srtPath: p.srt || '', filterPath: p.filter || '', imagePath: image || '', sourceSession: sessionStorage.getItem('__sessionId') || '' }),
            });
            setMessages(prev => [...prev, { role: 'model', text: res.ok ? `Sent "${p.name}" to the screen.` : `Couldn't send it to the screen (${res.status}).`, local: true }]);
        } catch (e) {
            setMessages(prev => [...prev, { role: 'model', text: `Can't reach server (${e?.message || 'network error'})`, local: true }]);
        }
    };

    const itemTitle = item ? titleOf(item.folder) : '';
    const hasSubtitles = !!item?.files.some(f => f.type === 'SRT');
    const [messages, setMessages] = useState(() => [
        item
            ? { role: 'model', text: `What would you like to do with "${itemTitle}"? I can find, download or translate its subtitles — or ask me anything about it.` }
            : remote
            ? { role: 'model', text: 'Tell me what to do on the screen — "play the next episode", "skip 30 seconds", "Arabic subtitles on", "play something funny"… Type it, or tap 🎤 and say it.' }
            : { role: 'model', text: 'Hi! Tell me what you feel like watching — a genre, an actor, a mood — and I\'ll show matching titles from your library. Tap one to play it. I remember your tastes between chats (see Memory).' },
        // A conversation already started elsewhere (Remote Control's quick 🎤 button).
        ...(initialMessages || []),
    ]);
    const suggestions = item
        ? ['Find English subtitles', 'Download Arabic subtitles', ...(hasSubtitles ? ['Translate the subtitles to Arabic'] : []), 'What is this about?']
        : remote ? ['Pause', 'Skip 30 seconds', 'Next episode', 'Turn on subtitles', 'Recommend something'] : [];
    const [recording, setRecording] = useState(null); // the recorder while the mic is on
    const [showMemory, setShowMemory] = useState(false);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // preset: text to send instead of the input box; audio: a spoken request instead of text.
    const send = async (preset, audio) => {
        const text = audio ? '' : (typeof preset === 'string' ? preset : input).trim();
        if ((!text && !audio) || loading) return;

        const userMsg = { role: 'user', text: audio ? '🎤 …' : text, voice: !!audio };
        const nextMessages = [...messages, userMsg];
        setMessages(nextMessages);
        setInput('');
        setLoading(true);

        // build history excluding the initial greeting and the message just sent
        const history = nextMessages.slice(1, -1).filter(m => !m.local && !(m.voice && m.text === '🎤 …')).map(m => ({ role: m.role, text: m.text.replace(/^🎤 /, '') }));

        try {
            const res = await authFetch(`${domain}/api/v1/ai/chat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...(audio ? { audio } : { message: text }),
                    history,
                    ...(item ? { item: item.folder } : {}),
                    ...(remote ? { mode: 'remote', screen: remote.getScreen() } : {}),
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Request failed');
            // Show what was heard in place of the "🎤 …" placeholder.
            if (audio) setMessages(prev => prev.map(m => (m === userMsg ? { ...m, text: `🎤 ${data.heard || '(couldn\'t make it out)'}` } : m)));
            setMessages(prev => [...prev, { role: 'model', text: data.reply, items: data.items || [], jobs: data.jobs || [], memoryChanged: !!data.memoryChanged }]);
            if (data.jobs?.length) window.dispatchEvent(new Event(TRANSLATE_EVENT)); // the Store's AI jobs panel lists it
            if (data.changed) onChanged?.();
            if (remote && data.actions?.length) await remote.runActions(data.actions);
        } catch (err) {
            setMessages(prev => [...prev, { role: 'model', text: `Error: ${err.message}` }]);
        } finally {
            setLoading(false);
        }
    };

    // 🎤: tap to start speaking, tap again to send (stops by itself after a few seconds).
    const toggleMic = async () => {
        if (recording) {
            setRecording(null);
            try { await send(null, await recording.stop()); }
            catch (e) { setMessages(prev => [...prev, { role: 'model', text: e.message, local: true }]); }
            return;
        }
        try {
            const rec = await startRecording({
                onAutoStop: async () => {
                    setRecording(null);
                    try { await send(null, await rec.done); }
                    catch (e) { setMessages(prev => [...prev, { role: 'model', text: e.message, local: true }]); }
                },
            });
            setRecording(rec);
        } catch (e) {
            setMessages(prev => [...prev, { role: 'model', text: micErrorMessage(e), local: true }]);
        }
    };
    useEffect(() => () => { recording?.stop().catch(() => {}); }, [recording]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
        if (e.key === 'Escape') onClose();
    };

    return (
        <div className="ai-chat-overlay" onClick={onClose}>
            <div className="ai-chat-panel" onClick={e => e.stopPropagation()}>
                <div className="ai-chat-header">
                    <span className="ai-chat-heading" title={item?.folder}>{item ? `AI · ${itemTitle}` : remote ? 'AI Remote' : 'AI Assistant'}</span>
                    <button className={`ai-chat-memory-btn${showMemory ? ' is-active' : ''}`} onClick={() => setShowMemory(v => !v)} title="What the AI remembers about you">Memory</button>
                    <button className="ai-chat-close" onClick={onClose}>✕</button>
                </div>
                {showMemory && <MemoryPanel domain={domain} />}
                <div className="ai-chat-messages">
                    {messages.map((m, i) => (
                        <div key={i} className={`ai-msg ai-msg-${m.role}`}>
                            <span className="ai-msg-label">{m.role === 'user' ? 'You' : 'AI'}</span>
                            {m.text && <span className="ai-msg-text">{m.text}</span>}
                            {m.items?.length > 0 && (
                                <div className="ai-cards">
                                    {m.items.map(it => <ResultCard key={it.folder} domain={domain} item={it} onPlay={play} />)}
                                </div>
                            )}
                            {m.jobs?.map(job => <JobStatus key={job.id} domain={domain} job={job} onDone={onChanged} />)}
                            {m.memoryChanged && <span className="ai-msg-note">🧠 Memory updated</span>}
                        </div>
                    ))}
                    {loading && (
                        <div className="ai-msg ai-msg-model">
                            <span className="ai-msg-label">AI</span>
                            <span className="ai-msg-text ai-typing">thinking…</span>
                        </div>
                    )}
                    {messages.length === 1 && suggestions.length > 0 && (
                        <div className="ai-suggestions">
                            {suggestions.map(sug => <button key={sug} className="ai-suggestion" onClick={() => send(sug)}>{sug}</button>)}
                        </div>
                    )}
                    <div ref={bottomRef} />
                </div>
                <div className="ai-chat-input-row">
                    <textarea
                        ref={inputRef}
                        className="ai-chat-input"
                        rows={2}
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder={recording ? 'Listening… tap 🎤 again when you\'re done' : item ? `Ask about ${itemTitle}… (Enter to send)` : remote ? 'Tell the screen what to do… (Enter to send)' : 'Ask for something to watch… (Enter to send)'}
                    />
                    {voiceSupported() && (
                        <button className={`ai-chat-mic${recording ? ' is-recording' : ''}`} onClick={toggleMic} disabled={loading && !recording}
                            title={recording ? 'Stop and send' : 'Speak instead of typing'} aria-label={recording ? 'Stop and send' : 'Speak'}>
                            🎤
                        </button>
                    )}
                    <button className="ai-chat-send" onClick={() => send()} disabled={loading}>Send</button>
                </div>
            </div>
        </div>
    );
}
